// $FlowFixMe
import { useCallback, useEffect, useState } from 'react';
import { pathOr } from 'ramda';
import { isNil } from 'lodash';
import messages from './messages';
import StateService from '../../../../services/stateService';
import UnitService from '../../../../services/unitService';

/**
 *
 * The state value in the Units table is stored as '<State>' and not '<Country-Code>-<State Code>'
 *
 */
const parseStateOptions = (arrOfUnparsedOptions: Array<Object>) => {
  const parsedStateOptions = arrOfUnparsedOptions.map(({ id, code }) => ({
    value: code,
    text: code,
  }));
  return parsedStateOptions;
};

export const useAsyncStateOptions = () => {
  const [unitOptions, setUnitOptions] = useState(null);

  useEffect(() => {
    // $FlowFixMe
    const abortController = new AbortController(); // eslint-disable-line
    let subscribed = true;

    const fetchFormOptions = async () => {
      const options = { signal: abortController.signal };
      if (!unitOptions) {
        const stateService = new StateService();
        const [statesRes] = await Promise.all([stateService.getAll(options)]);

        const stateOptions = parseStateOptions(statesRes);

        setUnitOptions({
          stateOptions,
        });
      }
    };

    if (subscribed) {
      fetchFormOptions();
    }

    return () => {
      subscribed = false;
    };
  }, [unitOptions, setUnitOptions]);
  return unitOptions;
};

export const useAsyncSaveUnit = (
  unitId: string,
  intl: Object,
  selectedProperty: Object,
  toasterFn: Function,
  refreshUnit: Function,
  getUnitById: Function,
) => {
  const [subscribed, setSubscribed] = useState(true);
  const [submitValues, setSubmitValues] = useState(null);

  const handleSaveUnit = (values: Object) => {
    const isADA = values.isADA === 'true';
    setSubmitValues({
      ...values,
      isADA,
      // Accessibility Types only apply to ADA units; a non-ADA unit clears them.
      accessibilityTypeIds: isADA ? values.accessibilityTypeIds ?? [] : [],
      // Convert empty string or undefined quotingRentAmount to null
      quotingRentAmount:
        isNil(values.quotingRentAmount) || values.quotingRentAmount === ''
          ? null
          : values.quotingRentAmount,
    });
  };

  const selectedPropertyId = pathOr(null, ['id'], selectedProperty);
  const organizationId = pathOr(null, ['organizationId'], selectedProperty);

  const fetchSaveUnit = useCallback(async () => {
    if (submitValues !== null && selectedPropertyId && organizationId) {
      // $FlowFixMe
      const abortController = new AbortController(); // eslint-disable-line
      const options = { signal: abortController.signal };
      const unitService = new UnitService();

      try {
        setSubmitValues(null);
        await unitService.updateUnit(
          selectedPropertyId,
          organizationId,
          unitId,
          submitValues,
          options,
        );

        // Update unit values in ViewUnit component
        refreshUnit(submitValues);

        // Re-fetch so association-based fields (e.g. accessibilityTypes) reflect
        // the saved state on the read-only screen, not just the merged scalars.
        if (getUnitById) {
          getUnitById(unitId);
        }

        if (subscribed) {
          toasterFn({
            title: intl.formatMessage(messages.success),
            message: intl.formatMessage(messages.updateUnitSuccess),
          });
        }
      } catch (error) {
        if (subscribed) {
          toasterFn({
            type: 'error',
            message: error.toString(),
            title: intl.formatMessage(messages.errorHeader),
          });
        }
      }
    }
  }, [
    unitId,
    submitValues,
    selectedPropertyId,
    organizationId,
    subscribed,
    intl,
    refreshUnit,
    getUnitById,
    toasterFn,
  ]);

  useEffect(() => {
    setSubscribed(true);
    fetchSaveUnit();
    return () => {
      setSubscribed(false);
    };
  }, [fetchSaveUnit]);

  return handleSaveUnit;
};
